import { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';

interface LinkedInTokenStatus {
  hasToken: boolean;
  isExpired: boolean;
  expiresAt: string | null;
  hasPostingAccess: boolean;
}

/**
 * Hook to fetch and cache LinkedIn token status
 * Returns token status and loading/error states
 */
export const useLinkedInTokenStatus = () => {
  const [status, setStatus] = useState<LinkedInTokenStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get('/users/linkedin-token-status');
      setStatus(response.data);
    } catch (err: unknown) {
      console.error('Failed to fetch LinkedIn token status:', err);
      setError(getErrorMessage(err));
      setStatus({
        hasToken: false,
        isExpired: false,
        expiresAt: null,
        hasPostingAccess: false,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return {
    status,
    hasToken: status?.hasToken || false,
    isExpired: status?.isExpired || false,
    expiresAt: status?.expiresAt,
    hasPostingAccess: status?.hasPostingAccess || false,
    isLoading,
    error,
    refetch: fetchStatus,
  };
};


